<!--
@component
Squads

Renders a team's coaching staff (above) and the squad players (below).

**Layout switches based on whether the rail overflows:**
  - **Overflows** — a horizontally-scrollable rail of individual
    position cards with a right-edge fade and a one-shot "swipe
    right" hint that disappears once the user scrolls.
  - **Fits** — a single rounded card with the positions sitting
    side-by-side, separated by thin vertical dividers. No
    scrolling, no fade, no hint.

The switch is driven entirely by overflow detection (no viewport
breakpoint), using the shared `useHorizontalScroll` composable
(originally written for the MatchesWidget). This avoids the awkward
left-aligned cluster of cards that a fixed breakpoint would produce
at viewports where the content happens to fit.
-->

<script lang="ts">
  import { ROLE, type Position, type SquadMember } from '../types';
  import { useHorizontalScroll } from '$lib/components/embeds/MatchesWidget/useHorizontalScroll.svelte';

  /** Position labels rendered, in the order they appear on the pitch. */
  const POSITIONS: Position[] = [
    'Goalkeeper',
    'Defender',
    'Midfielder',
    'Attacker',
  ];

  /** Full name from first + last; used for coaches and assistants. */
  const fullName = (m: SquadMember) =>
    `${m.firstName ?? ''} ${m.lastName ?? ''}`.trim();

  /** Stable-ish key for `{#each}` — prefers `matchName`, falls back to full
   *  name plus shirt number to disambiguate same-named players. */
  const playerKey = (m: SquadMember) =>
    `${m.matchName ?? fullName(m)}#${m.shirtNumber ?? ''}`;

  /** Sort comparator: ascending by shirt number, treating non-numeric as ∞. */
  const byShirt = (a: SquadMember, b: SquadMember) => {
    const an = Number(a.shirtNumber);
    const bn = Number(b.shirtNumber);
    return (
      (Number.isFinite(an) ? an : Infinity) -
      (Number.isFinite(bn) ? bn : Infinity)
    );
  };

  let { squad }: { squad: SquadMember[] } = $props();

  /** Safe accessor — components rendered before the loader resolves can pass `undefined`. */
  let members: SquadMember[] = $derived(Array.isArray(squad) ? squad : []);

  /**
   * Single-pass grouping: walks `members` once and bucketises coaches,
   * assistants, and players-by-position. Player buckets are then sorted
   * by shirt number so the rendered roster reads naturally (1, 2, 3, …).
   */
  let groups = $derived.by(() => {
    const coaches: string[] = [];
    const assistantCoaches: string[] = [];
    const byPosition: Record<Position, SquadMember[]> = {
      Goalkeeper: [],
      Defender: [],
      Midfielder: [],
      Attacker: [],
    };

    for (const m of members) {
      if (m.role === ROLE.coach) {
        coaches.push(fullName(m));
        continue;
      }
      if (m.role === ROLE.assistantCoach) {
        assistantCoaches.push(m.matchName ?? fullName(m));
        continue;
      }
      // Only count players who have been issued a shirt number. The explicit
      // null/empty-string check keeps `0` and `''` out without conflating
      // them with missing data.
      if (m.position && m.shirtNumber != null && m.shirtNumber !== '') {
        const bucket = byPosition[m.position as Position];
        if (bucket) bucket.push(m);
      }
    }

    for (const list of Object.values(byPosition)) list.sort(byShirt);

    return { coaches, assistantCoaches, byPosition };
  });

  /** Only render the positions that actually have players. */
  let positionGroups = $derived(
    POSITIONS.map((label) => ({
      label,
      players: groups.byPosition[label],
    })).filter((g) => g.players.length > 0)
  );

  let hasPlayers = $derived(positionGroups.length > 0);

  /* ------------------------------------------------------------------
   * Horizontal scroll wiring — same composable used by MatchRail.
   * Drives both the rail interactions (fade, swipe hint) and the
   * layout switch between scrollable rail and unified card.
   * ------------------------------------------------------------------ */
  const scroll = useHorizontalScroll({
    scrollFraction: 0.8,
    scrollCheckDelay: 300,
    edgeThreshold: 4,
  });

  /**
   * Whether we've taken at least one overflow measurement. We only
   * trust `hasOverflow === false` *after* the first measurement —
   * before that, the rail should render in its default (scrollable)
   * form so the browser can actually measure whether the content
   * overflows. Without this, the unified-card styling (which
   * constrains card widths) would apply on first paint and the rail
   * would never have a chance to overflow in the first place.
   */
  let measured = $state(false);

  $effect(() => {
    const cleanup = scroll.setupScrollMonitoring();
    measured = true;
    return cleanup;
  });

  // Re-check overflow when the rendered position list changes.
  $effect(() => {
    positionGroups.length;
    if (typeof requestAnimationFrame !== 'undefined') {
      requestAnimationFrame(() => scroll.checkOverflow());
    }
  });

  /** Right fade appears whenever there's more content to the right. */
  let showRightEdge = $derived(scroll.hasOverflow && !scroll.isAtEnd);
  /**
   * Swipe-right hint: only shown at the very start of the scroll
   * (before the user has interacted) and only when there's actually
   * overflow. Pressing it nudges the rail right one step; as soon as
   * the user scrolls, it disappears.
   */
  let showSwipeHint = $derived(scroll.hasOverflow && scroll.isAtStart);

  /**
   * When the rail content fits without overflowing, switch to the
   * unified-card layout. Drives the visual mode entirely — there's
   * no separate viewport breakpoint.
   */
  let noOverflow = $derived(measured && !scroll.hasOverflow);
</script>

{#snippet coachRow(label: string, value: string)}
  <div class="coach-row">
    <dt class="font-medium text-right text-secondary">{label}</dt>
    <dd>{value}</dd>
  </div>
{/snippet}

{#snippet playerRow(p: SquadMember)}
  <li class="player fmy-0 text-sm leading-tighter">
    <span
      class="shirt text-xxs font-medium text-secondary"
      aria-label="Shirt number {p.shirtNumber}"
    >
      <svg
        class="shirt-icon"
        viewBox="0 0 24 24"
        aria-hidden="true"
        focusable="false"
      >
        <!-- Stylised football jersey: shoulders, sleeves and torso. -->
        <path
          d="M8.5 3 4 5.2 2.2 9.6l3.3 1.7L6.5 9v11.2h11V9l1 2.3 3.3-1.7L20 5.2 15.5 3a3.6 3.6 0 0 1-7 0Z"
          fill="currentColor"
        />
      </svg>
      <span class="shirt-number">{p.shirtNumber}</span>
    </span>
    <span class="name ml-0.5">
      {p.matchName ?? fullName(p)}
    </span>
  </li>
{/snippet}

{#if groups.coaches.length > 0 || groups.assistantCoaches.length > 0}
  <dl class="coaches fmx-auto fmb-3 text-sm">
    {#if groups.coaches.length > 0}
      {@render coachRow('Manager:', groups.coaches.join(', '))}
    {/if}
    {#if groups.assistantCoaches.length > 0}
      {@render coachRow(
        groups.assistantCoaches.length > 1 ? 'Assistants:' : 'Assistant',
        groups.assistantCoaches.join(', ')
      )}
    {/if}
  </dl>
{/if}

{#if hasPlayers}
  <div
    class="squad-window"
    class:no-overflow={noOverflow}
    class:measuring={!measured}
  >
    <div class="squad-scroll" bind:this={scroll.contentElement}>
      <div class="squad-rail" aria-label="Squad">
        {#each positionGroups as group (group.label)}
          <section class="position-card">
            <h3 class="position-title fmt-0 fmb-3">
              <span
                class="position-label leading-none text-secondary uppercase text-xs tracking-wide"
                >{group.label}</span
              >
              <span class="position-count leading-none text-secondary text-xs"
                >{group.players.length}</span
              >
            </h3>
            <ul class="player-list">
              {#each group.players as player (playerKey(player))}
                {@render playerRow(player)}
              {/each}
            </ul>
          </section>
        {/each}
      </div>
    </div>

    <!-- Trailing-edge fade. Mobile only. Decorative; `pointer-events:
         none` so touch/wheel scrolling passes through to the rail. -->
    {#if showRightEdge}
      <div class="edge-fade right" aria-hidden="true"></div>
    {/if}

    <!-- Swipe-right hint. Mobile only. Only at the start of the scroll,
         only when there's overflow. Clicking it nudges the rail right
         by one "page" of cards via the composable. -->
    {#if showSwipeHint}
      <button
        type="button"
        class="swipe-hint"
        onclick={scroll.scrollRight}
        aria-label="Swipe right to see more positions"
      >
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <polyline points="9 18 15 12 9 6"></polyline>
        </svg>
      </button>
    {/if}
  </div>
{/if}

<style lang="scss">
  @use '../../../styles/mixins' as mixins;

  /* --------------------------------------------------------------------
   * Coaches: compact term/definition list anchored above the squad rail.
   * Identical at both breakpoints; only collapses to single-column on
   * very narrow viewports.
   * ------------------------------------------------------------------ */
  .coaches {
    display: grid;
    grid-template-columns: auto auto;
    gap: 0.25rem;
    width: max-content;
    max-width: 100%;
    margin-left: auto;
    margin-right: auto;

    .coach-row {
      display: contents;
    }

    dt {
      margin: 0;
      white-space: nowrap;
    }

    dd {
      margin: 0;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    @media (max-width: 480px) {
      grid-template-columns: 1fr;
      width: 100%;
      text-align: center;
      gap: 0.5rem 0;

      .coach-row {
        display: block;
      }

      dt {
        display: inline;
      }

      dt::after {
        content: ': ';
      }

      dd {
        display: inline;
      }
    }
  }

  /* ====================================================================
   * Default: horizontally-scrollable rail of position cards. Used
   * whenever the rail overflows its container.
   *
   *  .squad-window  — positioning context for fade overlays + hint
   *  .squad-scroll  — the scroll container (overflow-x: auto)
   *  .squad-rail    — the inline track of cards
   *  .position-card — equal-height card per position
   * ================================================================== */
  .squad-window {
    position: relative;
    width: 100%;
    /* Clip the fade overlays + swipe hint so they don't bleed beyond
       the rail. */
    overflow: hidden;
  }

  /* While the very first overflow measurement is pending, keep the
     rail laid out (so the browser can measure it) but hide it from
     view. This prevents the brief "left-aligned scrollable rail"
     flash before the layout settles on either the scrollable or
     unified-card variant. */
  .squad-window.measuring {
    visibility: hidden;
  }

  .squad-scroll {
    width: 100%;
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: none;
    -ms-overflow-style: none;
    &::-webkit-scrollbar {
      display: none;
    }

    /* Snap each card to the rail's leading edge. */
    scroll-snap-type: x proximity;
    scroll-behavior: smooth;
    scroll-padding-inline-start: 0.5rem;

    @media (prefers-reduced-motion: reduce) {
      scroll-behavior: auto;
      scroll-snap-type: none;
    }
  }

  .squad-rail {
    display: flex;
    flex-wrap: nowrap;
    align-items: stretch;
    gap: 0.35rem;
    width: max-content;
  }

  .position-card {
    flex: 0 0 auto;
    min-width: fit-content;
    max-width: max-content;
    @include mixins.fp-5;
    border-radius: var(--theme-style-border-radius);
    background-color: var(--theme-colour-muted-grey);
    display: flex;
    flex-direction: column;
    scroll-snap-align: start;
  }

  /* --- Overflow-only fade + swipe hint ----------------------------- */
  $edge-width: 3rem;

  .edge-fade {
    position: absolute;
    top: 0;
    bottom: 0;
    width: $edge-width;
    pointer-events: none; /* let touch swipes reach the rail */
    z-index: 1;

    &.right {
      right: 0;
      background: linear-gradient(
        to left,
        var(--theme-colour-background) 0%,
        rgba(255, 255, 255, 0) 100%
      );
    }
  }

  .swipe-hint {
    position: absolute;
    top: 50%;
    right: 0.5rem;
    transform: translateY(-50%);
    width: 2.25rem;
    height: 2.25rem;
    padding: 0;
    border: none;
    border-radius: 999px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: var(--theme-colour-white);
    color: var(--theme-colour-secondary);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
    z-index: 2;
    transition:
      color 0.15s ease,
      transform 0.15s ease,
      box-shadow 0.15s ease;

    /* Gentle horizontal nudge to hint at the swipe direction. */
    animation: swipe-nudge 1.6s ease-in-out infinite;
    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }

    &:hover {
      color: var(--theme-colour-text);
      box-shadow: 0 3px 8px rgba(0, 0, 0, 0.2);
    }

    &:focus-visible {
      outline: 2px solid var(--theme-colour-accent2);
      outline-offset: 2px;
    }
  }

  @keyframes swipe-nudge {
    0%,
    100% {
      transform: translate(0, -50%);
    }
    50% {
      transform: translate(-0.35rem, -50%);
    }
  }

  /* ====================================================================
   * Unified-card layout: single rounded card with vertical dividers
   * between positions. No scrolling, no fade, no hint. Applied
   * whenever the JS measures the rail and finds nothing to scroll —
   * regardless of viewport width.
   * ================================================================== */
  .squad-window.no-overflow {
    /* No more need to clip overlays. */
    overflow: visible;
    max-width: max-content;
    margin-inline: auto;

    .squad-scroll {
      /* Drop scrolling + snap; everything fits. */
      overflow: visible;
      padding: 0;
      scroll-snap-type: none;
    }

    .squad-rail {
      /* The rail itself becomes the single rounded card. */
      width: 100%;
      gap: 0;
      background-color: var(--theme-colour-muted-grey);
      border-radius: var(--theme-style-border-radius);
      box-shadow: 1px 1px 1px var(--theme-colour-tan-grey);
      justify-content: center;
    }

    .position-card {
      /* Cards now share the rail's background; no per-card shadow or
         pill. Vertical dividers separate them. */
      flex: 1 1 0;
      background-color: transparent;
      border-radius: 0;
      box-shadow: none;
      border-left: 1px solid var(--theme-colour-tan-grey);

      &:first-child {
        border-left: none;
      }
    }

    /* Fade + hint only make sense while the rail is scrollable. */
    .edge-fade,
    .swipe-hint {
      display: none;
    }
  }

  /* --------------------------------------------------------------------
   * Shared bits: position title, player list/row, jersey badge.
   * ------------------------------------------------------------------ */
  .position-title {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    @include mixins.font-sans;

    .position-count {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 1.25rem;
      height: 1.25rem;
      padding: 0 0.35rem; /* expands the pill for 3-digit counts */
      border-radius: 999px;
      background-color: var(--theme-colour-tan-grey);
      font-variant-numeric: tabular-nums;
      line-height: 1;
    }
  }

  .player-list {
    list-style: none;
    margin: 0;
    padding: 0;
    flex: 1; /* fill remaining card height */
    @include mixins.font-sans;
  }

  .player {
    display: flex;
    align-items: center;
    height: calc(1.5 * var(--theme-font-size-base));

    .shirt {
      position: relative;
      flex: none;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: calc(1.75 * var(--theme-font-size-base));
      height: calc(1.75 * var(--theme-font-size-base));
      text-align: center;
      font-variant-numeric: tabular-nums;
    }

    .shirt-icon {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      stroke: var(--theme-colour-white);
      color: var(--theme-colour-tan-grey);
      stroke-width: 2px;
      stroke-linecap: round;
      stroke-linejoin: round;
      pointer-events: none;
      z-index: 0;
    }

    .shirt-number {
      position: relative; /* sit above the jersey */
      z-index: 1;
      line-height: 1;
      /* Nudge down so the digit sits visually centred on the jersey's
         torso rather than the bounding box of the SVG. */
      transform: translateY(0.15em);
    }

    .name {
      min-width: 0;
      overflow-wrap: anywhere;
    }
  }
</style>
